import Seo from '@/components/base/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import { siteName, endingRoutes } from '@/mocks/siteData';

const typeColor: Record<string, string> = {
  真结局: 'text-primary-700 bg-primary-100 border-primary-200',
  好结局: 'text-accent-700 bg-accent-100 border-accent-200',
  隐藏结局: 'text-secondary-700 bg-secondary-100 border-secondary-200',
  普通结局: 'text-foreground-700 bg-foreground-100 border-foreground-200',
  坏结局: 'text-foreground-800 bg-foreground-950 border-foreground-950 text-background-50',
};

export default function EndingsPage() {
  const jsonLd: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '火影同人游戏多结局攻略',
      description: '分类整理真结局、好结局、隐藏结局、普通结局与坏结局的触发条件与走向。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: endingRoutes.map((e, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: `${e.type}「${e.title}」`,
        description: e.desc,
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`火影同人游戏结局攻略 - 多结局走向详解 | ${siteName}`}
        description="按真结局、好结局、隐藏结局、普通结局与坏结局分类整理火影同人游戏的走向，标注每个结局的触发条件与避免踩坑的要点。"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-28 md:px-6 md:pt-32">
        <nav aria-label="面包屑" className="mb-8 flex flex-wrap items-center gap-1 font-label text-xs text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">结局攻略</span>
        </nav>

        <div className="text-center">
          <p className="font-label text-sm font-bold tracking-widest text-accent-600">多结局攻略</p>
          <h1 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">火影同人游戏结局走向详解</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            每条结局路线都标注了触发条件与关键注意事项，帮助你规划多周目流程，把隐藏结局和真结局一一收入囊中。
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {['真结局', '好结局', '隐藏结局', '普通结局', '坏结局'].map((t) => (
            <span key={t} className={`rounded-md border px-3 py-1 font-label text-xs font-bold ${typeColor[t]}`}>{t}</span>
          ))}
        </div>

        <div className="mt-10 space-y-6">
          {endingRoutes.map((e, i) => (
            <article key={e.id} className="grid grid-cols-1 gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-[auto_1fr] md:p-8">
              <div className="flex flex-row items-center gap-4 md:flex-col md:items-center md:justify-start">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className={`${e.icon} text-2xl`}></i>
                </span>
                <div className="md:text-center">
                  <span className={`rounded-md border px-2.5 py-1 font-label text-xs font-bold ${typeColor[e.type]}`}>{e.type}</span>
                  <p className="mt-2 font-heading text-sm font-bold text-foreground-500">结局 {String(i + 1).padStart(2, '0')}</p>
                </div>
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{e.title}</h2>
                <p className="mt-1 text-sm text-foreground-500">{e.game}</p>
                <div className="mt-4 rounded-md bg-background-100 p-4">
                  <p className="font-label text-xs font-bold text-accent-600">
                    <i className="ri-git-branch-line mr-1"></i>触发条件
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-800">{e.cond}</p>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">{e.desc}</p>
                <p className="mt-4 flex items-start gap-2 text-sm text-primary-700">
                  <i className="ri-lightbulb-line mt-0.5"></i>
                  <span>{e.tips}</span>
                </p>
              </div>
            </article>
          ))}
        </div>
      </main>
      <Footer />
      <ScrollTopButton />
    </>
  );
}